import React, { useState, useEffect, useRef } from "react";
import { FaArrowRightLong, FaLeaf, FaSeedling, FaMicrochip } from "react-icons/fa6";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const Innovation: React.FC = () => {
  const { t } = useTranslation();
  const text = t("innovation_title");
  const [displayedText, setDisplayedText] = useState<string>("");
  const [isVisible, setIsVisible] = useState(false);
  const headingRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  // Typewriter effect
  useEffect(() => {
    let observer: IntersectionObserver;
    let interval: ReturnType<typeof setInterval>;

    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        let i = 0;
        interval = setInterval(() => {
          setDisplayedText(text.slice(0, i + 1));
          i++;
          if (i === text.length) clearInterval(interval);
        }, 50);
        observer.disconnect();
      }
    };

    observer = new IntersectionObserver(handleIntersect, { threshold: 0.5 });
    if (headingRef.current) observer.observe(headingRef.current);

    return () => {
      clearInterval(interval);
      observer.disconnect();
    };
  }, [text]);

  // Fade-in animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);

    return () => observer.disconnect();
  }, []);

  const features = [
    { icon: FaLeaf, text: t("innovation_feature_1") },
    { icon: FaMicrochip, text: t("innovation_feature_2") },
    { icon: FaSeedling, text: t("innovation_feature_3") },
  ];

  return (
    <div
      ref={sectionRef}
      className="relative w-full min-h-screen bg-[#ffffff] overflow-hidden"
    >


      <div className="relative max-w-7xl mx-auto px-5 sm:px-12 py-16 md:py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

          <div
            className={`space-y-6 transition-all duration-1000 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
              }`}
          >
            <div className="space-y-3 sm:space-y-4">
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight">
                <span className="text-[#002F3F]">{t("innovation_section_main")}</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#7EDE56] to-[#5BC83D]">
                  {t("innovation_section_sub")}
                </span>
              </h1>

              <div ref={headingRef} className="min-h-[4rem]">
                <h2 className="font-bold text-2xl sm:text-3xl md:text-4xl text-[#1C4734] leading-tight" aria-label={text}>
                  {displayedText}
                  <span className="animate-pulse">|</span>
                </h2>
              </div>
            </div>

            {/* Description */}
            <p className="text-md md:text-xl text-[#1C4734]/80 leading-relaxed max-w-xl">
              {t("innovation_description")}
            </p>

            {/* Feature Pills */}
            <div className="flex flex-wrap gap-3">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-[#7EDE56]/20 rounded-full shadow-sm hover:shadow-md hover:border-[#7EDE56] transition-all duration-300"
                >
                  <feature.icon className="text-[#7EDE56] text-sm" />
                  <span className="text-sm font-medium text-[#002F3F]">
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="pt-4">
              <Link
                to="/projects"
                className="group relative inline-flex items-center px-8 py-4 bg-[#7EDE56] text-[#002F3F] font-semibold text-lg rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
              >
                <span className="relative z-10 flex items-center gap-3 group-hover:text-white transition-colors duration-300">
                  Discover Projects
                  <FaArrowRightLong className="transition-transform duration-300 group-hover:translate-x-2" />
                </span>
                <div className="absolute inset-0 bg-[#002F3F] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </Link>
            </div>
          </div>

          {/* Right Image Section */}
          <div
            className={`relative transition-all duration-1000 delay-300 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
              }`}
          >
            {/* Decorative elements */}
            <div className="absolute -top-6 -left-6 w-24 h-24 bg-[#7EDE56]/20 rounded-3xl transform rotate-12"></div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-[#002F3F]/10 rounded-3xl transform -rotate-12"></div>

            {/* Image Container */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
              <div className="absolute inset-0 bg-gradient-to-tr from-[#7EDE56]/20 to-transparent z-10 pointer-events-none"></div>
              <img
                src="/innovation1.webp"
                loading="lazy"
                alt="Smart Greenhouse Technology"
                className="w-full h-full object-cover min-h-[400px] lg:min-h-[550px]"
              />
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Innovation;
